<template>
    <Modal
        :model-value="modelValue"
        draggable
        footer-hide
        scrollable
        transfer
        :mask="false"
        :width="width"
        :closable="closable"
        :class-name="classes"
        class="my-content"
        :z-index="zIndex"
        @onVisibleChange="onVisibleChange"
    >
        <template #header>
            <slot name="header">
                <div class="detachable-panel__header" @dblclick="onDoubleClick">
                    <span><slot name="title">Settings</slot></span>

                    <span v-if="attachable" class="detachable-panel__header-icon">
                        <Icon type="md-swap" @click.capture.stop.prevent="onAttach" />
                    </span>
                </div>
            </slot>
        </template>
        <div class="detachable-panel__content">
            <slot name="content" />
        </div>
    </Modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
    name: "DetachablePanel",
    props: {
        modelValue: {
            type: Boolean,
            required: true
        },

        className: {
            type: String
        },

        attachable: {
            type: Boolean,
            default: true
        },

        closable: {
            type: Boolean,
            default: true
        },

        width: {
            type: Number,
            default: 300
        }
    },

    emits: ["close", "attach"],

    data() {
        return {
            collapsedMinHeight: "49px",
            expandedMinHeight: "300px",
            expandedHeight: "300px",
            zIndex: 500
        };
    },

    computed: {
        classes(): string {
            const baseClasses = [
                "theme-campaign-dark",
                "dark-scrollbar",
                "detachable-panel",
                "detachable-panel__modal"
            ];

            if (this.closable) {
                baseClasses.push("detachable-panel--closable");
            }

            if (this.className) {
                baseClasses.push(this.className);
            }

            return baseClasses.join(" ");
        }
    },

    methods: {
        onAttach() {
            this.$emit("attach");
        },

        onVisibleChange(visible: boolean) {
            if (!visible) {
                this.$emit("close");
            }
        },

        onDoubleClick() {
            const contentEl = this.$el.querySelector(".ivu-modal-content") as HTMLElement;

            if (contentEl.style.minHeight === this.collapsedMinHeight) {
                contentEl.style.height = this.expandedHeight;
                contentEl.style.minHeight = this.expandedMinHeight;
            } else {
                this.expandedHeight = contentEl.style.height;
                contentEl.style.height = this.collapsedMinHeight;
                contentEl.style.minHeight = this.collapsedMinHeight;
            }
        }
    }
});
</script>
<style lang="scss">
@use "@style/variables" as *;

.detachable-panel {
    color: $cmp-dark-font-color;

    & .ivu-collapse-header {
        font-size: $font-size-base;
    }

    &__modal {
        overflow: hidden;
    }

    &__content {
        min-width: 200px;
        width: 100%;
        height: 100%;
    }

    &--closable &__header {
        padding-right: 24px;
    }

    .ivu-modal-content {
        box-shadow: 1px 1px 5px 0 #000000;
        background-color: $cmp-dark-bg-color;
        border-radius: $border-radius-base;
        resize: both;
        overflow: hidden;
        min-width: 200px;
        min-height: 300px;
        padding-bottom: 45px;
        height: 100%;
    }

    .ivu-modal {
        margin: 0 0 0 auto;
    }

    .ivu-modal-header {
        border-color: $cmp-dark-border-color;

        p,
        &-inner {
            color: $cmp-dark-font-color;
        }
    }

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 20px;

        &-icon {
            cursor: pointer;
            color: #999;

            &:hover {
                color: #444;
            }
        }
    }

    .ivu-modal-body {
        padding: 0;
        height: 100%;
    }
}
</style>
